import {
        getZonesList,
        getZone,
        createZone as createZoneApi,
        updateZone as updateZoneApi,
        activateZone,
        deleteZone as deleteZoneApi,
} from '@/api/zones'

function decodeDump(encoded) {
        try {
                const json = decodeURIComponent(escape(atob(encoded)))
                return JSON.parse(json)
        } catch (e) {
                console.error('Error decoding zone data:', e)
                return []
        }
}

function encodeDump(data) {
        const json = JSON.stringify(data)
        return btoa(unescape(encodeURIComponent(json)))
}

const state = {
        zonesList: [],
        loadedZones: {},
        activeZoneId: null,
        hasUnsavedChanges: false,
        pendingZoneActivation: null,
}

const mutations = {
        SET_ZONES_LIST(state, zones) {
                state.zonesList = zones
        },

        SET_ZONE_DATA(state, { zoneId, data }) {
                state.loadedZones = {
                        ...state.loadedZones,
                        [zoneId]: data,
                }
        },

        SET_ACTIVE_ZONE(state, zoneId) {
                state.activeZoneId = zoneId
        },

        SET_PENDING_ZONE_ACTIVATION(state, zoneId) {
                state.pendingZoneActivation = zoneId
        },

        SET_UNSAVED_CHANGES(state, value) {
                state.hasUnsavedChanges = value
        },

        ADD_ZONE(state, zone) {
                state.zonesList.push(zone)
        },

        UPDATE_ZONE_IN_LIST(state, { zoneId, title, totalPrice }) {
                const zone = state.zonesList.find(z => z.id === zoneId)
                if (zone) {
                        if (title !== undefined) zone.title = title
                        if (totalPrice !== undefined) zone.total_price = totalPrice
                }
        },

        DELETE_ZONE_FROM_LIST(state, zoneId) {
                state.zonesList = state.zonesList.filter(z => z.id !== zoneId)
                delete state.loadedZones[zoneId]
                if (state.activeZoneId === zoneId) {
                        state.hasUnsavedChanges = false
                }
        },

        CLEAR_ZONES(state) {
                state.zonesList = []
                state.loadedZones = {}
                state.activeZoneId = null
                state.hasUnsavedChanges = false
                state.pendingZoneActivation = null
        },

        SYNC_ACTIVE_ZONE_ELEMENTS(state, { elements, background, groups }) {
                if (!state.activeZoneId) return
                state.loadedZones = {
                        ...state.loadedZones,
                        [state.activeZoneId]: {
                                elements: elements || [],
                                background: background || null,
                                groups: groups || [],
                        },
                }
                const computedTotal = (elements || []).reduce((sum, item) => sum + (Number(item.price) || 0), 0)
                const zone = state.zonesList.find(z => z.id === state.activeZoneId)
                if (zone) {
                        zone.total_price = computedTotal
                }
        },
}

const actions = {
        async loadZonesList({ commit }, projectId) {
                try {
                        const response = await getZonesList(projectId)
                        if (response.success) {
                                commit('SET_ZONES_LIST', response.zones)
                                return response.zones
                        }
                        throw new Error('Failed to load zones list')
                } catch (error) {
                        console.error('Error loading zones list:', error)
                        throw error
                }
        },

        async loadZone({ state, commit }, zoneId) {
                if (state.loadedZones[zoneId]) {
                        return state.loadedZones[zoneId]
                }

                try {
                        const response = await getZone(zoneId)
                        if (response.success) {
                                const decodedData = decodeDump(response.zone.data)
                                commit('SET_ZONE_DATA', {
                                        zoneId,
                                        data: decodedData,
                                })
                                return decodedData
                        }
                        throw new Error('Failed to load zone data')
                } catch (error) {
                        console.error('Error loading zone:', error)
                        throw error
                }
        },

        async setActiveZone({ commit, dispatch, state }, zoneId) {
                try {
                        commit('SET_PENDING_ZONE_ACTIVATION', zoneId)
                        await dispatch('loadZone', zoneId)

                        if (state.pendingZoneActivation !== zoneId) {
                                return
                        }

                        await activateZone(zoneId)

                        if (state.pendingZoneActivation !== zoneId) {
                                return
                        }

                        commit('SET_ACTIVE_ZONE', zoneId)
                        commit('SET_UNSAVED_CHANGES', false)
                        commit('SET_PENDING_ZONE_ACTIVATION', null)
                } catch (error) {
                        console.error('Error in setActiveZone:', error)
                        commit('SET_PENDING_ZONE_ACTIVATION', null)
                        throw error
                }
        },

        async createZone({ commit }, { projectId, zoneId, title, data = [] }) {
                try {
                        const response = await createZoneApi({
                                project_id: projectId,
                                zone_id: zoneId,
                                title,
                                data: encodeDump(data),
                        })

                        if (response.success) {
                                commit('ADD_ZONE', response.zone)
                                commit('SET_ZONE_DATA', {
                                        zoneId: response.zone.id,
                                        data,
                                })
                                return response.zone
                        }
                        throw new Error('Failed to create zone')
                } catch (error) {
                        console.error('Error creating zone:', error)
                        throw error
                }
        },

        async updateZone({ commit }, { zoneId, title, data }) {
                try {
                        const payload = {}
                        if (title !== undefined) payload.title = title
                        if (data !== undefined) payload.data = encodeDump(data)

                        const response = await updateZoneApi(zoneId, payload)

                        if (response.success) {
                                commit('UPDATE_ZONE_IN_LIST', { 
                                        zoneId, 
                                        title: title !== undefined ? title : undefined,
                                })
                                if (data !== undefined) {
                                        commit('SET_ZONE_DATA', { zoneId, data })
                                }
                                commit('SET_UNSAVED_CHANGES', false)
                                return response.zone
                        }
                        throw new Error('Failed to update zone')
                } catch (error) {
                        console.error('Error updating zone:', error)
                        throw error
                }
        },

        async deleteZone({ commit }, zoneId) {
                try {
                        const response = await deleteZoneApi(zoneId)
                        if (response.success) {
                                commit('DELETE_ZONE_FROM_LIST', zoneId)
                                return true
                        }
                        throw new Error('Failed to delete zone')
                } catch (error) {
                        console.error('Error deleting zone:', error)
                        throw error
                }
        },

        markDirty({ commit }) {
                commit('SET_UNSAVED_CHANGES', true)
        },

        syncZoneElements({ commit }, { elements, background, groups }) {
                commit('SYNC_ACTIVE_ZONE_ELEMENTS', { elements, background, groups })
        },

        clearZones({ commit }) {
                commit('CLEAR_ZONES')
        },
}

const getters = {
        activeZoneId: state => state.activeZoneId,
        
        activeZone: state => {
                if (!state.activeZoneId) return null
                const zoneInfo = state.zonesList.find(z => z.id === state.activeZoneId)
                const zoneData = state.loadedZones[state.activeZoneId]
                return zoneInfo && zoneData
                        ? { ...zoneInfo, data: zoneData }
                        : null
        },

        getZoneById: state => zoneId => {
                const zoneInfo = state.zonesList.find(z => z.id === zoneId)
                const zoneData = state.loadedZones[zoneId]
                return zoneInfo && zoneData
                        ? { ...zoneInfo, data: zoneData }
                        : zoneInfo
        },

        hasLoadedZone: (state) => (zoneId) => {
                return !!state.loadedZones[zoneId]
        },

        getZonePrice: (state) => (zoneId) => {
                const zoneData = state.loadedZones[zoneId]
                if (!zoneData) return 0
                const elements = zoneData.elements || zoneData || []
                if (!Array.isArray(elements)) return 0
                return elements.reduce((sum, item) => sum + (Number(item.price) || 0), 0)
        },

        activeZonePrice: (state, getters) => {
                if (!state.activeZoneId) return 0
                return getters.getZonePrice(state.activeZoneId)
        },

        totalProjectPrice: (state) => {
                return state.zonesList.reduce((total, zone) => total + (Number(zone.total_price) || 0), 0)
        },
}

export default {
        namespaced: true,
        state,
        mutations,
        actions,
        getters,
}
